import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Calendar, Filter, Eye, User, CheckCircle, Clock, AlertCircle, Edit, Trash2, ArrowLeft } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const TaskView = ({ user }) => {
  const navigate = useNavigate();
  const [teamUsers, setTeamUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]); // For resolving picked_by user IDs
  const [selectedUserId, setSelectedUserId] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  useEffect(() => {
    fetchTeamUsers();
    fetchAllUsers();
  }, []);

  const fetchTeamUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/users/team`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Remove duplicates by ID
      const uniqueUsers = response.data.filter((user, index, self) =>
        index === self.findIndex(u => u.id === user.id)
      );
      setTeamUsers(uniqueUsers);
      
      // Auto-select current user if team member
      if (user.role === 'team_member' && uniqueUsers.length > 0) {
        setSelectedUserId(uniqueUsers[0].id);
        setSelectedUser(uniqueUsers[0]);
      }
    } catch (error) {
      toast.error('Failed to fetch team users');
    }
  };

  const fetchAllUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Remove duplicates by ID
      const uniqueUsers = response.data.filter((user, index, self) =>
        index === self.findIndex(u => u.id === user.id)
      );
      setAllUsers(uniqueUsers);
    } catch (error) {
      console.error('Failed to fetch all users');
    }
  };

  const handleUserSelect = (userId) => {
    setSelectedUserId(userId);
    const userObj = teamUsers.find(u => u.id === userId);
    setSelectedUser(userObj);
    setTasks([]); // Clear previous tasks
  };

  const handleViewTasks = async () => {
    if (!selectedUserId) {
      toast.error('Please select a user');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      
      if (statusFilter !== 'all') {
        params.append('status', statusFilter);
      }
      if (startDate) {
        params.append('start_date', startDate);
      }
      if (endDate) {
        params.append('end_date', endDate);
      }

      const response = await axios.get(
        `${API_URL}/api/tasks/user/${selectedUserId}?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setTasks(response.data);
      toast.success(`Loaded ${response.data.length} tasks`);
    } catch (error) {
      toast.error('Failed to fetch tasks');
    } finally {
      setLoading(false);
    }
  };

  const getUserName = (userId) => {
    const userObj = allUsers.find(u => u.id === userId);
    return userObj ? userObj.full_name : 'Unknown User';
  };

  const getPickedByUsers = (pickedByIds) => {
    if (!pickedByIds || pickedByIds.length === 0) return 'No one picked yet';
    return pickedByIds.map(id => getUserName(id)).join(', ');
  };

  const handleDeleteTask = async (task) => {
    // Check if task has assignments
    const hasAssignedUser = task.assigned_to !== null;
    const hasPickedUsers = task.picked_by && task.picked_by.length > 0;
    
    let confirmMessage = 'Are you sure you want to delete this task?';
    
    if (hasAssignedUser || hasPickedUsers) {
      confirmMessage = '⚠️ This task has assignments that will be removed:\n\n';
      if (hasAssignedUser) {
        const assignedUser = getUserName(task.assigned_to);
        confirmMessage += `• Assigned to: ${assignedUser}\n`;
      }
      if (hasPickedUsers) {
        confirmMessage += `• Picked by ${task.picked_by.length} user(s)\n`;
      }
      confirmMessage += '\nAll assignments will be removed. Do you want to proceed?';
    }
    
    if (!window.confirm(confirmMessage)) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/tasks/${task.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Task deleted successfully!');
      handleViewTasks(); // Refresh tasks
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete task');
    }
  };

  const handleEditTask = (taskId) => {
    navigate(`/dashboard/tasks/edit/${taskId}`);
  };

  const getPriorityColor = (priority) => {
    // Priority is now a number 1-10
    if (priority >= 8) return '#f44336'; // High priority (8-10)
    if (priority >= 5) return '#ff9800'; // Medium priority (5-7)
    return '#4caf50'; // Low priority (1-4)
  };

  const getPriorityLabel = (priority) => {
    // Display the numeric priority
    return `Priority ${priority}`;
  };

  const getDeadlineStatus = (deadline, status) => {
    if (!deadline) return null;
    
    const now = new Date();
    const deadlineDate = new Date(deadline);
    const diffDays = Math.ceil((deadlineDate - now) / (1000 * 60 * 60 * 24));
    
    if (status === 'completed') {
      return {
        label: 'Completed',
        color: '#4caf50',
        icon: '✓'
      };
    }
    
    if (diffDays < 0) {
      return {
        label: `Overdue by ${Math.abs(diffDays)} day${Math.abs(diffDays) === 1 ? '' : 's'}`,
        color: '#f44336',
        icon: '⚠️'
      };
    }
    
    if (diffDays === 0) {
      return {
        label: 'Due Today',
        color: '#ff9800',
        icon: '🔔'
      };
    }
    
    if (diffDays <= 2) {
      return {
        label: `Due in ${diffDays} day${diffDays === 1 ? '' : 's'}`,
        color: '#ff9800',
        icon: '⏰'
      };
    }
    
    return {
      label: `Due in ${diffDays} days`,
      color: '#4caf50',
      icon: '📅'
    };
  };

  const formatDeadlineDate = (deadline) => {
    if (!deadline) return null;
    return new Date(deadline).toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle size={18} style={{ color: '#4caf50' }} />;
      case 'in_progress':
        return <Clock size={18} style={{ color: '#ff9800' }} />;
      default:
        return <AlertCircle size={18} style={{ color: '#2196f3' }} />;
    }
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'completed':
        return '#4caf50';
      case 'in_progress':
        return '#ff9800';
      default:
        return '#2196f3';
    }
  };

  const tasksByStatus = {
    all: tasks,
    completed: tasks.filter(t => t.status === 'completed'),
    in_progress: tasks.filter(t => t.status === 'in_progress'),
    pending: tasks.filter(t => t.status === 'pending')
  };

  return (
    <div style={{ padding: '32px', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Header with Back button */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
        <button
          onClick={() => navigate('/dashboard')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 16px',
            background: 'var(--bg-secondary)',
            border: '1px solid var(--border-color)',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '600',
            color: 'var(--text-primary)',
            transition: 'all 0.2s'
          }}
        >
          <ArrowLeft size={18} />
          Back
        </button>
        <h1 style={{ fontSize: '32px', fontWeight: '700', margin: 0, color: '#1a1a1a' }}>
          Task View
        </h1>
      </div>

      {/* Filters Section */}
      <div style={{
        background: 'white',
        padding: '24px',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
        marginBottom: '24px'
      }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '20px',
          marginBottom: '20px'
        }}>
          {/* User Selection */}
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px' }}>
              <User size={16} style={{ display: 'inline', marginRight: '6px' }} />
              Select User
            </label>
            <select
              value={selectedUserId}
              onChange={(e) => handleUserSelect(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: '8px',
                border: '1px solid #ddd',
                fontSize: '14px'
              }}
              disabled={user.role === 'team_member'}
            >
              <option value="">-- Select User --</option>
              {teamUsers.map(u => (
                <option key={u.id} value={u.id}>
                  {u.full_name} ({u.role})
                </option>
              ))}
            </select>
          </div>

          {/* Start Date */}
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px' }}>
              <Calendar size={16} style={{ display: 'inline', marginRight: '6px' }} />
              Start Date (Optional)
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: '8px',
                border: '1px solid #ddd',
                fontSize: '14px'
              }}
            />
          </div>

          {/* End Date */}
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px' }}>
              <Calendar size={16} style={{ display: 'inline', marginRight: '6px' }} />
              End Date (Optional)
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: '8px',
                border: '1px solid #ddd',
                fontSize: '14px'
              }}
            />
          </div>

          {/* Status Filter */}
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px' }}>
              <Filter size={16} style={{ display: 'inline', marginRight: '6px' }} />
              Status Filter
            </label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: '8px',
                border: '1px solid #ddd',
                fontSize: '14px'
              }}
            >
              <option value="all">All Tasks</option>
              <option value="pending">Pending</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
            </select>
          </div>
        </div>

        {/* View Button */}
        <button
          onClick={handleViewTasks}
          disabled={loading || !selectedUserId}
          className="gradient-btn"
          style={{
            padding: '12px 32px',
            fontSize: '15px',
            cursor: loading || !selectedUserId ? 'not-allowed' : 'pointer',
            opacity: loading || !selectedUserId ? 0.6 : 1
          }}
        >
          <Eye size={18} />
          {loading ? 'Loading...' : 'View Tasks'}
        </button>
      </div>

      {/* Selected User Info */}
      {selectedUser && tasks.length > 0 && (
        <div style={{
          background: '#E7EDF9',
          padding: '20px',
          borderRadius: '12px',
          marginBottom: '24px'
        }}>
          <h3 style={{ margin: '0 0 12px 0', fontSize: '18px', color: 'var(--text-primary)' }}>
            Viewing tasks for: <span style={{ color: 'var(--color-primary)' }}>{selectedUser.full_name}</span>
          </h3>
          <div style={{ display: 'flex', gap: '24px', fontSize: '14px', color: '#666' }}>
            <span><strong>Role:</strong> {selectedUser.role}</span>
            <span><strong>Email:</strong> {selectedUser.email}</span>
            <span><strong>Total Tasks:</strong> {tasks.length}</span>
          </div>
        </div>
      )}

      {/* Task Statistics */}
      {tasks.length > 0 && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '16px',
          marginBottom: '24px'
        }}>
          <div style={{
            background: 'white',
            padding: '20px',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
            borderLeft: '4px solid #2196f3'
          }}>
            <div style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>Pending</div>
            <div style={{ fontSize: '28px', fontWeight: '700', color: '#2196f3' }}>
              {tasksByStatus.pending.length}
            </div>
          </div>

          <div style={{
            background: 'white',
            padding: '20px',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
            borderLeft: '4px solid #ff9800'
          }}>
            <div style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>In Progress</div>
            <div style={{ fontSize: '28px', fontWeight: '700', color: '#ff9800' }}>
              {tasksByStatus.in_progress.length}
            </div>
          </div>

          <div style={{
            background: 'white',
            padding: '20px',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
            borderLeft: '4px solid #4caf50'
          }}>
            <div style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>Completed</div>
            <div style={{ fontSize: '28px', fontWeight: '700', color: '#4caf50' }}>
              {tasksByStatus.completed.length}
            </div>
          </div>
        </div>
      )}

      {/* Tasks List */}
      {tasks.length > 0 ? (
        <div style={{
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
          overflow: 'hidden'
        }}>
          <div style={{
            padding: '20px',
            borderBottom: '1px solid #f0f0f0',
            background: '#f9f9ff'
          }}>
            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>
              Tasks List ({tasks.length})
            </h3>
          </div>

          <div style={{ padding: '20px' }}>
            {tasks.map((task) => (
              <div
                key={task.id}
                onClick={() => navigate(`/dashboard/tasks/${task.id}`)}
                style={{
                  padding: '16px',
                  marginBottom: '12px',
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  transition: 'all 0.2s',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#7077FE';
                  e.currentTarget.style.backgroundColor = '#f9f9ff';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#e0e0e0';
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', gap: '16px' }}>
                  <div style={{ flex: 1 }}>
                    <h4 style={{ margin: '0 0 8px 0', fontSize: '16px', fontWeight: '600' }}>
                      {task.title}
                    </h4>
                    
                    {/* Show Assigned By information */}
                    {task.assigned_by && (
                      <div style={{
                        marginBottom: '8px',
                        padding: '6px 10px',
                        background: '#FFF4E6',
                        borderRadius: '6px',
                        border: '1px solid #FFE0B2',
                        display: 'inline-block'
                      }}>
                        <span style={{ fontSize: '12px', fontWeight: '600', color: '#F57C00' }}>
                          👤 Assigned By: {getUserName(task.assigned_by)}
                        </span>
                      </div>
                    )}
                    
                    <div 
                      style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#666' }}
                      dangerouslySetInnerHTML={{ __html: task.description }}
                    />
                    
                    {/* Show picked_by users for common tasks */}
                    {(!task.assigned_to || (Array.isArray(task.assigned_to) && task.assigned_to.length === 0)) && task.picked_by && task.picked_by.length > 0 && (
                      <div style={{
                        marginBottom: '12px',
                        padding: '8px 12px',
                        background: '#f0f7ff',
                        borderRadius: '6px',
                        border: '1px solid #7077FE30'
                      }}>
                        <span style={{ fontSize: '13px', fontWeight: '600', color: '#7077FE' }}>
                          👥 Picked by:
                        </span>
                        <span style={{ fontSize: '13px', color: '#555', marginLeft: '8px' }}>
                          {getPickedByUsers(task.picked_by)}
                        </span>
                        <span style={{ fontSize: '12px', color: '#888', marginLeft: '8px' }}>
                          ({task.picked_by.length} {task.picked_by.length === 1 ? 'person' : 'people'})
                        </span>
                      </div>
                    )}
                    
                    <div style={{ display: 'flex', gap: '16px', fontSize: '13px', color: '#888', flexWrap: 'wrap', alignItems: 'center' }}>
                      <span>📅 {new Date(task.created_at).toLocaleDateString()}</span>
                      {task.attachments && task.attachments.length > 0 && (
                        <span>📎 {task.attachments.length} attachment{task.attachments.length > 1 ? 's' : ''}</span>
                      )}
                      <span style={{
                        padding: '4px 10px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: '600',
                        backgroundColor: getPriorityColor(task.priority) + '20',
                        color: getPriorityColor(task.priority),
                        border: `1px solid ${getPriorityColor(task.priority)}`
                      }}>
                        {getPriorityLabel(task.priority)} Priority
                      </span>
                    </div>
                    
                    {/* Deadline Display */}
                    {task.deadline && (
                      <div style={{ marginTop: '8px' }}>
                        <span style={{
                          padding: '6px 12px',
                          borderRadius: '8px',
                          fontSize: '13px',
                          fontWeight: '600',
                          backgroundColor: getDeadlineStatus(task.deadline, task.status).color + '20',
                          color: getDeadlineStatus(task.deadline, task.status).color,
                          border: `1px solid ${getDeadlineStatus(task.deadline, task.status).color}`,
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}>
                          <span>{getDeadlineStatus(task.deadline, task.status).icon}</span>
                          <span>{getDeadlineStatus(task.deadline, task.status).label}</span>
                          <span style={{ opacity: 0.7, fontSize: '12px' }}>
                            ({formatDeadlineDate(task.deadline)})
                          </span>
                        </span>
                      </div>
                    )}
                  </div>
                  
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'flex-end' }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '6px 12px',
                      borderRadius: '20px',
                      background: `${getStatusBadgeColor(task.status)}15`,
                      color: getStatusBadgeColor(task.status),
                      fontSize: '13px',
                      fontWeight: '600'
                    }}>
                      {getStatusIcon(task.status)}
                      {task.status.replace('_', ' ')}
                    </div>
                    
                    {user.role !== 'team_member' && (
                      <div style={{ display: 'flex', gap: '6px' }}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditTask(task.id);
                          }}
                          title="Edit task"
                          style={{
                            padding: '8px',
                            background: 'none',
                            border: '1px solid #7077FE',
                            borderRadius: '6px',
                            color: '#7077FE',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = '#7077FE';
                            e.currentTarget.style.color = 'white';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'none';
                            e.currentTarget.style.color = '#7077FE';
                          }}
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteTask(task);
                          }}
                          title="Delete task"
                          style={{
                            padding: '8px',
                            background: 'none',
                            border: '1px solid #d32f2f',
                            borderRadius: '6px',
                            color: '#d32f2f',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = '#d32f2f';
                            e.currentTarget.style.color = 'white';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'none';
                            e.currentTarget.style.color = '#d32f2f';
                          }}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : selectedUserId && !loading ? (
        <div style={{
          background: 'white',
          padding: '60px',
          borderRadius: '12px',
          textAlign: 'center',
          boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
        }}>
          <p style={{ fontSize: '16px', color: '#888' }}>No tasks found for the selected filters</p>
        </div>
      ) : null}
    </div>
  );
};

export default TaskView;
